import { useParams, Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { EvidenceGallery } from '@/components/EvidenceGallery';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import type { ReturnDisposition } from '@/api/types';

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). Status, disposition, discrepancy,
 * and evidence per line, per the epic's own spec for this screen. Evidence
 * is scoped per return LINE (`entityKind: 'INBOUND_LINE'`) since that is
 * the actual evidence-attachment granularity ADR-074 established - a
 * gallery per line, not one gallery for the whole return.
 */
export function ReturnDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const result = useApi(() => endpoints.returnDetail(id!), [id]);
  const { t, locale } = useLanguage();

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const { return: ret, lines, dispositions } = result.data;
  const dispositionsByLine = new Map<string, ReturnDisposition[]>();
  for (const d of dispositions) {
    const list = dispositionsByLine.get(d.inbound_line_id) ?? [];
    list.push(d);
    dispositionsByLine.set(d.inbound_line_id, list);
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t('returnDetail.title', { number: ret.number })}</h1>
        <Link to="/returns">{t('returnDetail.backToReturns')}</Link>
      </div>

      <div className="card">
        <dl className="kv">
          <dt>{t('common.status')}</dt>
          <dd>
            <StatusBadge value={ret.state} />
          </dd>
          <dt>{t('returnDetail.arrived')}</dt>
          <dd>{ret.arrived_at ? formatDateTime(ret.arrived_at, locale) : '—'}</dd>
          <dt>{t('dashboard.colCreated')}</dt>
          <dd>{formatDateTime(ret.created_at, locale)}</dd>
        </dl>
      </div>

      {lines.map((line) => (
        <div className="card" key={line.id}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>
            {line.sku_code ?? t('returnDetail.unknownSku')} — {line.sku_name ?? ''}
          </h2>
          <dl className="kv">
            <dt>{t('returnDetail.expected')}</dt>
            <dd>{formatNumber(line.qty_expected, locale)}</dd>
            <dt>{t('returnDetail.received')}</dt>
            <dd>{formatNumber(line.qty_received, locale)}</dd>
            <dt>{t('dashboard.discrepancy')}</dt>
            <dd>
              <StatusBadge value={line.discrepancy} />
            </dd>
          </dl>
          {(dispositionsByLine.get(line.id) ?? []).length > 0 && (
            <>
              <h3 style={{ fontSize: 13, marginBottom: 4 }}>{t('returnDetail.disposition')}</h3>
              <div className="table-scroll"><table>
                <thead>
                  <tr>
                    <th>{t('returnDetail.disposition')}</th>
                    <th style={{ textAlign: 'right' }}>{t('returnDetail.qty')}</th>
                    <th>{t('common.note')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(dispositionsByLine.get(line.id) ?? []).map((d) => (
                    <tr key={d.id} style={{ cursor: 'default' }}>
                      <td>
                        <StatusBadge value={d.disposition} />
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatNumber(d.qty, locale)}</td>
                      <td>{d.note ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </>
          )}
          <h3 style={{ fontSize: 13, marginTop: 12, marginBottom: 4 }}>{t('orderDetail.evidence')}</h3>
          <EvidenceGallery entityKind="INBOUND_LINE" entityId={line.id} />
        </div>
      ))}
    </div>
  );
}
